import {Decimal} from 'decimal.js';

import {isNil, isNotNil} from 'ramda';

import {NonNegativeBigDecimalSchema, type Money} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {calculateFinancingAmounts} from 'features/financial-products';

import type {VehicleFinancingPricing} from '../types/VehicleFinancingPricing';

export type FinancingCalculationResult = {
  monthlyRate: number;
  downPaymentAmount: number;
  netCredit: number;
  balloonAmount: number;
  totalInterest: number;
  // Null while the processing fee is unknown: the backend adds it to both figures.
  grossCredit: number | null;
  totalAmount: number | null;
};

// The monthly insurance of a financing configuration, priced only when `included`.
export interface FinancingInsurance {
  included: boolean;
  liabilityMonthly: Money | Nullish;
  comprehensiveMonthly: Money | Nullish;
}

interface CalculateFinancingOfferParams {
  // Null while the vehicle has no price to finance.
  pricing: VehicleFinancingPricing | null;
  term: number;
  downPayment: number;
  balloonPayment: number | null;
  nominalRate: string | number;
  insurance?: FinancingInsurance;
}

// Sums and products of money amounts stay exact at this precision. A clone, so the global
// `Decimal` keeps its own settings.
const InsuranceDecimal = Decimal.clone({precision: 64});

// The included monthly insurance in the calculation currency, zero when it is not included, or
// null when an included amount is unusable (another currency or not a non-negative decimal).
const getIncludedInsuranceMonthly = (
  insurance: FinancingInsurance | undefined,
  currency: string
) => {
  if (isNil(insurance) || !insurance.included) {
    return new InsuranceDecimal(0);
  }

  const amounts = [insurance.liabilityMonthly, insurance.comprehensiveMonthly].filter(isNotNil);
  const isUsable = amounts.every(
    (money) =>
      money.currency === currency && NonNegativeBigDecimalSchema.safeParse(money.amount).success
  );

  return isUsable
    ? amounts.reduce((sum, money) => sum.plus(money.amount), new InsuranceDecimal(0))
    : null;
};

// The vehicle-detail view of a financing offer: the shared backend-exact calculator prices the
// credit (processing fee included), and the monthly insurance is layered on top of it. Insurance
// is paid alongside the installment, not lent, so it stays out of `netCredit`, `grossCredit` and
// `totalInterest`, and enters `monthlyRate` and, once per month of the term, `totalAmount`.
//
// Null when the calculator refuses the offer (including a balloon that does not stay below the
// net credit) or an included insurance amount is unusable. An unknown processing fee prices the
// credit without it and leaves `grossCredit` and `totalAmount` null; no other figure contains it.
export function calculateFinancingOffer(
  params: CalculateFinancingOfferParams
): FinancingCalculationResult | null {
  if (isNil(params.pricing)) {
    return null;
  }

  const insuranceMonthly = getIncludedInsuranceMonthly(
    params.insurance,
    params.pricing.vehiclePriceWithVat.currency
  );

  if (isNil(insuranceMonthly)) {
    return null;
  }

  const isProcessingFeeKnown = params.pricing.processingFee !== 'UNKNOWN';
  const calculation = calculateFinancingAmounts({
    vehiclePriceWithVat: params.pricing.vehiclePriceWithVat,
    processingFee: params.pricing.processingFee === 'UNKNOWN' ? null : params.pricing.processingFee,
    termMonths: params.term,
    downPaymentPercentage: params.downPayment,
    balloonPaymentPercentage: params.balloonPayment,
    nominalRate: params.nominalRate,
    apr: null,
  });

  if (
    isNil(calculation) ||
    isNil(calculation.downPaymentAmount) ||
    isNil(calculation.netCredit) ||
    isNil(calculation.grossCredit) ||
    isNil(calculation.totalInterest) ||
    isNil(calculation.totalPayable)
  ) {
    return null;
  }

  return {
    monthlyRate: insuranceMonthly.plus(calculation.monthlyPayment.amount).toNumber(),
    downPaymentAmount: Number(calculation.downPaymentAmount.amount),
    netCredit: Number(calculation.netCredit.amount),
    balloonAmount: Number(calculation.balloonPaymentAmount?.amount ?? 0),
    totalInterest: Number(calculation.totalInterest.amount),
    grossCredit: isProcessingFeeKnown ? Number(calculation.grossCredit.amount) : null,
    totalAmount: isProcessingFeeKnown
      ? insuranceMonthly.times(params.term).plus(calculation.totalPayable.amount).toNumber()
      : null,
  };
}
